class HeaderSlideshow extends HTMLElement {
  constructor() {
    super()
    this.currentSlide = 0
    this.totalSlides = 0
    this.visibleSlides = 3
    this.animationDuration = 450
    this.slider = null
    this.slides = []
    this.bulletButtons = []
    this.timer = null
    this.previousSlide = 0
    this.leftAddedSldeWidth = 0
    this.isInfinite = false
  }

  connectedCallback() {
    this.initializeProperties()
    this.setupSlides()
    this.addEventListeners()
    this.updateSlideshow(0)
    this.resetAutoPlay()
  }

  initializeProperties() {
    this.slider = this.querySelector('.slideshow-slider')
    this.slides = Array.from(this.querySelectorAll('.slideshow-slide'))
    this.totalSlides = this.slides.length

    this.autoPlay = this.slider.dataset.autoPlay === 'true'
    this.autoPlayInterval = +this.slider.dataset.autoPlayInterval || 3000
  }

  setupSlides() {
    this.calculateSlideWidths()

    if (this.slideWidthsSum > this.slider.offsetWidth) {
      for (let i = 0; i < this.visibleSlides; i++) {
        this.slider.prepend(this.slides[this.totalSlides - 1 - i].cloneNode(true))
        this.slider.append(this.slides[i].cloneNode(true))

        this.leftAddedSldeWidth += this.slides[this.totalSlides - 1 - i].offsetWidth
      }
      this.isInfinite = true
    }

    this.updateSlideshow(0)
  }

  calculateSlideWidths() {
    this.slideWidths = this.slides.map((slide) => slide.offsetWidth)
    this.slideWidthsSum = this.slideWidths
      .slice(0, this.totalSlides)
      .reduce((acc, width) => acc + width, 0)
  }

  addEventListeners() {
    window.addEventListener('resize', this.handleResize.bind(this))

    this.slider.addEventListener('transitionend', () => {
      if (this.currentSlide < 0) {
        this.currentSlide = this.modulo(this.currentSlide, this.totalSlides)
      }

      if (this.currentSlide >= this.totalSlides) this.currentSlide = 0
      this.updateSlideshow(0)
    })

    const prevButton = this.querySelector('.slideshow-prev')
    const nextButton = this.querySelector('.slideshow-next')

    if (prevButton) prevButton.addEventListener('click', this.prevSlide.bind(this))
    if (nextButton) nextButton.addEventListener('click', this.nextSlide.bind(this))

    const slideshowNavigation = document.querySelector('.slideshow-navigation')
    const buttons = slideshowNavigation.querySelectorAll('.slideshow-bullet')

    buttons.forEach((button) => {
      const slideIndex = parseInt(button.getAttribute('data-slide-index'))
      button.addEventListener('click', () => {
        this.goToSlide(slideIndex)
      })

      this.bulletButtons.push(button)
    })
  }

  prevSlide() {
    this.previousSlide = this.modulo(this.currentSlide, this.totalSlides)
    if (this.currentSlide == -1) {
      this.currentSlide = this.modulo(this.currentSlide, this.totalSlides)
      this.updateSlideshow(0)
    }
    this.currentSlide -= 1
    if (!this.isInfinite) {
      this.currentSlide = this.modulo(this.currentSlide, this.totalSlides)
    }
    this.updateSlideshow()
    this.previousSlide = this.modulo(this.currentSlide, this.totalSlides)
    this.resetAutoPlay()
  }

  nextSlide() {
    this.previousSlide = this.modulo(this.currentSlide, this.totalSlides)
    if (this.currentSlide == this.totalSlides) {
      this.currentSlide = this.modulo(this.currentSlide, this.totalSlides)
      this.updateSlideshow(0)
    }
    this.currentSlide += 1
    if (!this.isInfinite) {
      this.currentSlide = this.modulo(this.currentSlide, this.totalSlides)
    }
    this.updateSlideshow()
    this.previousSlide = this.modulo(this.currentSlide, this.totalSlides)
    this.resetAutoPlay()
  }

  goToSlide(index) {
    if (index === this.previousSlide) return
    const slideChange = Math.abs(this.previousSlide - index)
    if (slideChange > this.totalSlides / 2 && this.isInfinite) {
      if (index < this.previousSlide) {
        this.currentSlide = this.modulo(index, this.totalSlides)
      } else {
        this.currentSlide = this.minusModulo(index, this.totalSlides)
      }
    } else {
      this.currentSlide = index
    }

    this.updateSlideshow()
    this.previousSlide = this.modulo(this.currentSlide, this.totalSlides)
    this.resetAutoPlay()
  }

  startAutoPlay() {
    if (!this.autoPlay) return
    this.stopAutoPlay()
    this.timer = setInterval(() => {
      this.nextSlide()
    }, this.autoPlayInterval)
  }

  stopAutoPlay() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  resetAutoPlay() {
    if (!this.autoPlay) return
    this.stopAutoPlay()
    this.startAutoPlay()
  }

  updateSlideshow(animationDuration = this.animationDuration) {
    const translateXPercentage = this.caculateTranslateDistance(
      this.currentSlide,
      this.previousSlide
    )
    const modSlide = this.modulo(this.currentSlide, this.totalSlides)

    const middlePercentage = ((this.slideWidths[modSlide] / this.slider.offsetWidth) * 100) / 2

    const middleTranslate = -translateXPercentage + 50 - middlePercentage

    this.slider.style.transitionDuration = `${animationDuration}ms`
    this.slider.style.transform = `translateX(${middleTranslate}%)`

    this.slides.forEach((slide, index) => slide.classList.toggle('is-active', modSlide === index))
    this.bulletButtons.forEach((button, index) =>
      button.classList.toggle('is-active', modSlide === index)
    )
  }

  modulo(n, m) {
    return ((n % m) + m) % m
  }

  minusModulo(n, m) {
    return ((n % m) - m) % m
  }

  caculateTranslateDistance(index, previousIndex) {
    let totalWidthBefore = 0
    const addedDistance = this.leftAddedSldeWidth

    if (index < 0) {
      index = this.modulo(index, this.totalSlides)
      totalWidthBefore = -this.slideWidths
        .slice(index, this.slideWidths.length)
        .reduce((acc, width) => acc + width, 0)
    } else {
      totalWidthBefore = this.slideWidths.slice(0, index).reduce((acc, width) => acc + width, 0)
    }

    const slideChange = Math.abs(previousIndex - index)
    if (slideChange > this.totalSlides / 2 && this.isInfinite) {
      if (previousIndex > index) {
        totalWidthBefore =
          this.slideWidthsSum +
          this.slideWidths.slice(0, index).reduce((acc, width) => acc + width, 0)
      }
    }

    const translateXPercentage =
      ((totalWidthBefore + addedDistance) / this.slider.offsetWidth) * 100
    return translateXPercentage
  }

  handleResize() {
    this.calculateSlideWidths()
    this.updateSlideshow(0)
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize.bind(this))
    this.stopAutoPlay()
  }
}

customElements.define('header-slideshow', HeaderSlideshow)
